/**
 * App shell matching the original app's layout: a dark sidebar against the
 * page, with the same nav item states, in light and in dark.
 *
 * Colours come from TOKENS in theme.ts, whose values live in tokens.css.
 * Nothing here writes a hex value of its own: that habit is what let the old
 * app's light and dark values drift apart.
 */
import {
  ActionIcon, AppShell, Avatar, Burger, Button, Group, Input, Kbd, Menu, Text, Tooltip, UnstyledButton,
} from '@mantine/core';
import { useDisclosure, useLocalStorage, useMediaQuery, useOs } from '@mantine/hooks';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  IconCheck, IconChecklist, IconDeviceDesktop, IconMoon, IconSun, IconChartBar, IconChevronDown, IconChevronRight,
  IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand,
  IconLogout, IconSearch, IconSparkles,
} from '@tabler/icons-react';
import { Link, NavLink as RouterLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTaskCounts } from '../api/activities';
import { APPEARANCE_OPTIONS, useAppearance, useSyncAppearance, type Appearance } from '../appearance';
import { FONTS, TOKENS } from '../theme';
import { navLabel, roleLabel, useVocab } from '../vocab';
import './sidebar.css';
import { AssistantPanel, loadConversation, type AssistantTurn } from './Assistant';
import { CommandBar, openCommandBar } from './CommandBar';
import { entryFor, linkOf, mayOpen, navFor, railActive, railFor, type NavGroup } from './nav';
import { useNavUser } from './navUser';
import { TemplateOnboarding } from './TemplateOnboarding';

/** Height of the top bar. */
const TOP = 48;

/** Rail widths: the full labelled sidebar, and the icon strip. */
const WIDE = 175;
const NARROW = 60;
/** Left edge of every 18px icon, which centres it in the narrow strip. */
const ICON_X = (NARROW - 18) / 2;

/*
  Shared by nav links and Sign out. The label is always rendered and only
  fades, so the icon sits at ICON_X whether the rail is narrow or wide, and
  the item under the pointer is still under it after the rail opens.

  Metrics lifted from the old Alpine app's `.nav-item`: 0.55rem vertical
  padding, 0.8125rem text, 18px icon, 0.65rem gap. Full-bleed, no radius:
  the old app's hover and active states span the whole rail.
*/
const itemStyle = (active: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.65rem',
  width: '100%',
  padding: `0.55rem 1rem 0.55rem ${ICON_X}px`,
  fontSize: '0.8125rem',
  fontWeight: 500,
  whiteSpace: 'nowrap' as const,
  color: active ? TOKENS.sidebarActiveText : TOKENS.sidebarText,
  background: active ? TOKENS.sidebarActiveBg : 'transparent',
});

function NavItem({
  to,
  label,
  icon: Icon,
  active,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: typeof IconChartBar;
  active: boolean;
  onNavigate: () => void;
}) {
  return (
    <UnstyledButton
      component={RouterLink}
      to={to}
      onClick={onNavigate}
      className="hf-rail-item"
      data-active={active}
      style={itemStyle(active)}
    >
      <Icon size={18} style={{ opacity: active ? 1 : 0.7, flex: 'none' }} />
      <span className="hf-rail-text">{label}</span>
    </UnstyledButton>
  );
}

export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const v = useVocab();
  /*
    A sheet is the one screen that wants the whole width. Forty columns of a
    spreadsheet squeezed beside a nav rail is the shape of the problem the
    viewer exists to solve, so on a sheet the rail goes away entirely and the
    page carries its own way back. The top bar stays, so the Assistant and
    the account menu are still there.
  */
  const { signOut } = useAuth();
  // The session's user plus whether the organisation has money to show, so
  // Returns and Statements appear where a stream sells.
  const user = useNavUser();
  const location = useLocation();
  // Light, dark or match system, as this person last chose it anywhere.
  useSyncAppearance();
  // Every page this person may open, and the ones with a rail item.
  const nav = navFor(user);
  const rail = railFor(user);
  const shown = rail.flatMap((g) => g.items);
  // Typing a hidden page's address sends you home rather than to a page of
  // empty tables or permission errors.
  const hidden = !mayOpen(nav, location.pathname);
  const inSheet = /^\/sheets\/[^/]+/.test(location.pathname);
  const current = entryFor(location.pathname, location.search);
  const pageName = current ? navLabel(v, current.labelKey) : '';

  const initials = (user?.email ?? '?').slice(0, 2).toUpperCase();

  // My overdue and due-today tasks, for the top bar's tasks button, for
  // anyone who may open Tasks. Polled each minute, so a task falling due
  // shows up. Tasks has no rail item: the button is always there instead.
  const seesTasks = nav.some((g) => g.items.some((i) => i.to === '/tasks'));
  const taskCounts = useTaskCounts(seesTasks).data;
  const tasksDue = taskCounts
    ? { count: taskCounts.overdue + taskCounts.today, urgent: taskCounts.overdue > 0 }
    : undefined;

  /*
    The rail can shrink to an icon strip, which hands about 115px back to the
    page: two or three more columns on the leads table. Icons only on the
    desktop; the phone drawer always shows labels, because it covers the page
    anyway. The choice is remembered per browser.
  */
  const [narrowPref, setNarrowPref] = useLocalStorage<boolean>({
    key: 'hawfinch_nav_compact',
    defaultValue: true,
  });
  const isDesktop = useMediaQuery('(min-width: 48em)') ?? true;
  const compact = narrowPref && isDesktop;

  /*
    Hovering the narrow rail opens it over the page without moving the page:
    the slot stays 60px and the full panel is uncovered on top. Two delays
    stop it fluttering: it opens only once the pointer rests on the rail, so
    sweeping across it does nothing, and it waits a beat before closing, so
    drifting a few pixels off the edge does not snap it shut. Keyboard focus
    opens it at once, since a tab stop on a hidden label is no use.
  */
  const OPEN_DELAY = 200;
  const CLOSE_DELAY = 300;
  const [peek, setPeek] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const clearTimer = () => window.clearTimeout(timer.current);
  const startPeek = () => {
    if (!compact) return;
    clearTimer();
    timer.current = window.setTimeout(() => setPeek(true), OPEN_DELAY);
  };
  const endPeek = () => {
    clearTimer();
    timer.current = window.setTimeout(() => setPeek(false), CLOSE_DELAY);
  };
  useEffect(() => () => clearTimer(), []);
  // Leaving narrow mode, or moving to another page, ends any peek.
  useEffect(() => {
    clearTimer();
    setPeek(false);
  }, [compact, location.pathname]);
  const collapsed = compact && !peek;

  /*
    The Set up, Money and More groups fold away. Each one's state is
    remembered in this browser; More starts folded. A group holding the page
    you are on stays open, so the current page is never hidden.
  */
  const [folded, setFolded] = useLocalStorage<Record<string, boolean>>({
    key: 'hawfinch_nav_groups',
    defaultValue: {},
  });
  const isFolded = (g: NavGroup) =>
    !!g.collapsible
    && (folded[g.key] ?? !!g.startCollapsed)
    && !g.items.some((i) => railActive(i, location.pathname, location.search, shown));
  const toggleGroup = (g: NavGroup) => setFolded((f) => ({ ...f, [g.key]: !isFolded(g) }));

  // The assistant, for the roles the server lets use it (admin and super
  // admin); everyone else sees no button at all rather than one that says no.
  // Its conversation is kept here so it survives moving between pages while
  // the panel is closed.
  const canAsk = user?.role === 'admin' || user?.role === 'super_admin';
  const [askOpen, setAskOpen] = useLocalStorage<boolean>({ key: 'hawfinch_assistant_open', defaultValue: false });
  const [turns, setTurns] = useState<AssistantTurn[]>(() => loadConversation());
  const showAsk = canAsk && askOpen;

  return (
    <AppShell
      // The rail runs the full height beside the top bar, so the logo keeps
      // its place at the top of it.
      layout="alt"
      header={{ height: TOP }}
      navbar={{
        width: compact ? NARROW : WIDE,
        breakpoint: 'sm',
        collapsed: { mobile: !opened || inSheet, desktop: inSheet },
      }}
      aside={{ width: 400, breakpoint: 'sm', collapsed: { desktop: !showAsk, mobile: !showAsk } }}
      padding={inSheet ? "xs" : "lg"}
      styles={{ main: { backgroundColor: TOKENS.bg } }}
    >
      <AppShell.Header
        style={{ background: TOKENS.surface, borderBottom: `1px solid ${TOKENS.border}` }}
      >
        <TopBar
          pageName={pageName}
          burger={!inSheet && (
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="Open menu" />
          )}
          searchHint={`Search ${v.many('lead')}, pages and more`}
          tasks={seesTasks}
          tasksDue={seesTasks ? tasksDue : undefined}
          tasksWord={v.many('task')}
          canAsk={canAsk}
          askOpen={showAsk}
          onAsk={() => setAskOpen(!askOpen)}
          email={user?.email ?? ''}
          role={roleLabel(v, user?.role)}
          initials={initials}
          onSignOut={signOut}
        />
      </AppShell.Header>

      <AppShell.Navbar
        p={0}
        onMouseEnter={startPeek}
        onMouseLeave={endPeek}
        onFocus={() => { if (compact) { clearTimer(); setPeek(true); } }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) endPeek();
        }}
        style={{
          background: TOKENS.sidebar,
          borderRight: `1px solid ${TOKENS.sidebarBorder}`,
          // The panel inside is wider than this slot while it peeks.
          overflow: 'visible',
          zIndex: 200,
        }}
      >
        <div
          className="hf-rail"
          data-collapsed={collapsed}
          data-peek={compact && peek}
          style={{
            width: compact ? WIDE : '100%',
            background: TOKENS.sidebar,
            borderRight: compact ? `1px solid ${TOKENS.sidebarBorder}` : undefined,
          }}
        >
          {/*
            The Rowstorm mark stays put in the narrow strip and the wordmark
            fades in beside it, so the logo never swaps or jumps. The mark is a
            white asset and the word is white text: both only work against the
            dark sidebar. The word is set in Funnel Display 800, the face the
            Rowstorm wordmark uses, which is already bundled for the app. The
            row is the top bar's height, so the two line up.
          */}
          <div style={{ position: 'relative', height: TOP, flex: 'none' }}>
            <img
              src="/rowstorm-mark.svg"
              alt="Rowstorm"
              style={{
                position: 'absolute', left: (NARROW - 26) / 2, top: (TOP - 20) / 2,
                width: 26, height: 20, display: 'block',
              }}
            />
            <span
              aria-hidden
              className="hf-rail-text"
              style={{
                position: 'absolute', left: NARROW - 6, top: '50%', transform: 'translateY(-50%)',
                font: `800 17px/1 ${FONTS.display}`, letterSpacing: '-0.01em',
                color: TOKENS.sidebarTextBright, whiteSpace: 'nowrap', userSelect: 'none',
              }}
            >
              rowstorm
            </span>
            <div
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}
            >
              <Burger
                opened={opened}
                onClick={toggle}
                hiddenFrom="sm"
                size="sm"
                color={TOKENS.sidebarText}
                aria-label="Close menu"
              />
              {isDesktop && (
                <Tooltip label={compact ? 'Keep menu open' : 'Collapse menu'} position="right" withArrow>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    className="hf-rail-text"
                    onClick={() => setNarrowPref(!compact)}
                    aria-label={compact ? 'Keep menu open' : 'Collapse menu'}
                    style={{ color: TOKENS.sidebarText }}
                  >
                    {compact ? <IconLayoutSidebarLeftExpand size={17} /> : <IconLayoutSidebarLeftCollapse size={17} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </div>
          </div>

          <nav
            aria-label="Main"
            style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '0.25rem 0 0.75rem' }}
          >
            {rail.map((group, gi) => {
              const isClosed = isFolded(group);
              const listId = `hf-nav-${group.key}`;
              return (
                <div key={group.key} data-testid={`nav-group-${group.key}`}>
                  {/* A heading and the narrow divider share one fixed-height
                      box, so the items below never shift between states. A
                      group without a heading gets a shorter gap. A folding
                      group's heading is the button that folds it. */}
                  {(group.heading || gi > 0) && (
                    <div style={{ position: 'relative', height: group.heading ? 30 : 14 }}>
                      <div
                        aria-hidden
                        className="hf-rail-divider"
                        style={{ background: TOKENS.sidebarBorder }}
                      />
                      {group.heading && (
                        <UnstyledButton
                          component={group.collapsible ? 'button' : 'div'}
                          className="hf-rail-text"
                          onClick={group.collapsible ? () => toggleGroup(group) : undefined}
                          aria-expanded={group.collapsible ? !isClosed : undefined}
                          aria-controls={group.collapsible ? listId : undefined}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 4,
                            paddingLeft: ICON_X,
                            paddingBottom: '0.35rem',
                            cursor: group.collapsible ? 'pointer' : 'default',
                          }}
                        >
                          <Text
                            component="span"
                            fw={700}
                            tt="uppercase"
                            style={{
                              fontSize: '0.6875rem',
                              color: TOKENS.sidebarLabel,
                              letterSpacing: '0.1em',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {navLabel(v, group.heading)}
                          </Text>
                          {group.collapsible && (
                            isClosed
                              ? <IconChevronRight size={12} style={{ color: TOKENS.sidebarLabel, marginBottom: 1 }} />
                              : <IconChevronDown size={12} style={{ color: TOKENS.sidebarLabel, marginBottom: 1 }} />
                          )}
                        </UnstyledButton>
                      )}
                    </div>
                  )}
                  <div id={listId} hidden={isClosed}>
                    {group.items.map((entry) => (
                      <NavItem
                        key={entry.to}
                        to={linkOf(entry)}
                        label={navLabel(v, entry.labelKey)}
                        icon={entry.icon}
                        active={railActive(entry, location.pathname, location.search, shown)}
                        onNavigate={close}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
        </div>
      </AppShell.Navbar>

      {showAsk && (
        <AppShell.Aside p={0} style={{ borderLeft: `1px solid ${TOKENS.border}` }}>
          <AssistantPanel turns={turns} setTurns={setTurns} onClose={() => setAskOpen(false)} />
        </AppShell.Aside>
      )}

      <AppShell.Main>
        {hidden ? <Navigate to="/" replace /> : <Outlet />}
      </AppShell.Main>

      <CommandBar />
      <TemplateOnboarding />
    </AppShell>
  );
}

/*
  The bar across the top of every page: where you are, a search box, and
  your account. The search box is a button dressed as a field: clicking it,
  or pressing Enter or Space on it, opens the command bar, which is where
  the typing happens. On a phone there is no room for the field, so a search
  icon does the same.
*/
function TopBar({
  pageName,
  burger,
  searchHint,
  tasks,
  tasksDue,
  tasksWord,
  canAsk,
  askOpen,
  onAsk,
  email,
  role,
  initials,
  onSignOut,
}: {
  pageName: string;
  burger: ReactNode;
  searchHint: string;
  /** Whether this person may open Tasks at all. */
  tasks: boolean;
  tasksDue?: { count: number; urgent: boolean };
  tasksWord: string;
  canAsk: boolean;
  askOpen: boolean;
  onAsk: () => void;
  email: string;
  role: string;
  initials: string;
  onSignOut: () => void;
}) {
  // The shortcut as this machine spells it.
  const os = useOs();
  const shortcut = os === 'macos' || os === 'ios' ? '⌘K' : 'Ctrl K';
  return (
    <Group h="100%" px="md" gap="md" wrap="nowrap" justify="space-between">
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: '1 1 0' }}>
        {burger}
        <Text fw={600} size="sm" truncate data-testid="page-name">{pageName}</Text>
      </Group>

      <Input
        component="button"
        type="button"
        pointer
        visibleFrom="sm"
        size="xs"
        w={360}
        onClick={openCommandBar}
        aria-label={searchHint}
        aria-haspopup="dialog"
        aria-keyshortcuts="Meta+K Control+K"
        leftSection={<IconSearch size={14} />}
        rightSection={<Kbd size="xs">{shortcut}</Kbd>}
        rightSectionWidth={shortcut.length > 2 ? 58 : 44}
        rightSectionPointerEvents="none"
        styles={{ input: { background: TOKENS.surface2, borderColor: 'transparent', textAlign: 'left' } }}
      >
        <Text component="span" size="xs" c="dimmed" truncate style={{ display: 'block' }}>{searchHint}</Text>
      </Input>

      <Group gap="xs" wrap="nowrap" justify="flex-end" style={{ flex: '1 1 0' }}>
        <ActionIcon
          hiddenFrom="sm"
          size="lg"
          variant="default"
          onClick={openCommandBar}
          aria-label={searchHint}
          aria-haspopup="dialog"
        >
          <IconSearch size={16} />
        </ActionIcon>
        {/* Tasks has no rail item, so this is how to reach it: a count
            when something is due, and a plain icon otherwise. */}
        {tasks && (!tasksDue || tasksDue.count === 0) && (
          <Tooltip label={`My ${tasksWord}`} withArrow>
            <ActionIcon
              component={Link}
              to="/tasks"
              size="lg"
              variant="default"
              data-testid="tasks-link"
              aria-label={`My ${tasksWord}`}
            >
              <IconChecklist size={16} />
            </ActionIcon>
          </Tooltip>
        )}
        {tasks && tasksDue && tasksDue.count > 0 && (
          <Tooltip label={`${tasksDue.count} ${tasksWord} overdue or due today`} withArrow>
            <Button
              component={Link}
              to="/tasks"
              size="xs"
              variant={tasksDue.urgent ? 'light' : 'default'}
              color={tasksDue.urgent ? 'red' : undefined}
              data-testid="tasks-badge"
              aria-label={`${tasksDue.count} ${tasksWord} overdue or due today`}
              leftSection={<IconChecklist size={14} />}
              px={10}
            >
              {tasksDue.count}
            </Button>
          </Tooltip>
        )}
        {canAsk && (
          <>
            <Button
              visibleFrom="sm"
              size="xs"
              variant={askOpen ? 'filled' : 'default'}
              leftSection={<IconSparkles size={14} />}
              onClick={onAsk}
              aria-pressed={askOpen}
            >
              Assistant
            </Button>
            {/* A phone has no room for the word. */}
            <ActionIcon
              hiddenFrom="sm"
              size="lg"
              variant={askOpen ? 'filled' : 'default'}
              onClick={onAsk}
              aria-pressed={askOpen}
              aria-label="Assistant"
            >
              <IconSparkles size={16} />
            </ActionIcon>
          </>
        )}
        <Menu position="bottom-end" width={240} withinPortal>
          <Menu.Target>
            <UnstyledButton aria-label="Account" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Avatar size={28} radius="xl" color="dark">{initials}</Avatar>
              <IconChevronDown size={14} style={{ color: TOKENS.textMuted }} />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            <div style={{ padding: '0.4rem 0.75rem 0.5rem' }}>
              <Text size="sm" fw={600} truncate>{email}</Text>
              <Text size="xs" c="dimmed">{role}</Text>
            </div>
            <Menu.Divider />
            <AppearanceItems />
            <Menu.Divider />
            <Menu.Item leftSection={<IconLogout size={14} />} onClick={onSignOut}>
              Sign out
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}

const APPEARANCE_ICONS: Record<Appearance, typeof IconSun> = {
  light: IconSun,
  dark: IconMoon,
  system: IconDeviceDesktop,
};

/** Light, dark or match system, in the account menu. The current one is ticked. */
function AppearanceItems() {
  const appearance = useAppearance();
  return (
    <>
      <Menu.Label>Appearance</Menu.Label>
      {APPEARANCE_OPTIONS.map(({ value, label }) => {
        const Icon = APPEARANCE_ICONS[value];
        const on = appearance.value === value;
        return (
          <Menu.Item
            key={value}
            leftSection={<Icon size={14} />}
            rightSection={on ? <IconCheck size={14} /> : null}
            aria-checked={on}
            role="menuitemradio"
            closeMenuOnClick={false}
            onClick={() => appearance.set(value)}
          >
            {label}
          </Menu.Item>
        );
      })}
    </>
  );
}
